import type React from "react";
import { useMemo } from "react";
import { CustomMarkdown } from "@/components/common/CustomMarkdown";
import {
  type extractImageReferences,
  InlineImagePreview,
} from "./InlineImagePreview";

interface ContentWithInlineImagesProps {
  content: string;
  references: ReturnType<typeof extractImageReferences>;
  className?: string;
  enableEmojiShortcodeCompat?: boolean;
}

export const ContentWithInlineImages: React.FC<
  ContentWithInlineImagesProps
> = ({
  content,
  references,
  className = "",
  enableEmojiShortcodeCompat = false,
}) => {
  const { contentParts, imageReferences } = useMemo(() => {
    if (references.length === 0) {
      // 画像参照がない場合は元のコンテンツを返す
      return { contentParts: [content], imageReferences: [] };
    }

    // 画像参照でコンテンツを分割
    const parts: string[] = [];

    let lastIndex = 0;
    references.forEach((ref, index) => {
      const startIndex = content.indexOf(ref.fullMatch, lastIndex);
      if (startIndex > lastIndex) {
        parts.push(content.substring(lastIndex, startIndex));
      }
      parts.push(`__IMAGE_PLACEHOLDER_${index}__`);
      lastIndex = startIndex + ref.fullMatch.length;
    });

    // 最後の画像参照の後のコンテンツを追加
    if (lastIndex < content.length) {
      parts.push(content.substring(lastIndex));
    }

    return { contentParts: parts, imageReferences: references };
  }, [content, references]);

  return (
    <div className={className}>
      {contentParts.map((part, index) => {
        // この部分が画像プレースホルダーかどうかをチェック
        const match = part.match(/__IMAGE_PLACEHOLDER_(\d+)__/);
        if (match) {
          const imageIndex = parseInt(match[1], 10);
          const imageRef = imageReferences[imageIndex];
          return (
            <InlineImagePreview
              key={`image-${imageIndex}`}
              guid={imageRef.guid}
              fileName={imageRef.fileName}
              className="my-2"
            />
          );
        }

        // テキスト部分はマークダウンとしてレンダリング
        if (part.trim()) {
          return (
            <div
              key={`text-${
                // biome-ignore lint/suspicious/noArrayIndexKey: false positive
                index
              }`}
            >
              <CustomMarkdown
                content={part}
                enableEmojiShortcodeCompat={enableEmojiShortcodeCompat}
              />
            </div>
          );
        }

        return null;
      })}
    </div>
  );
};
